<template>
    <div>
        <gd-modal name="transaction-detail-modal" width="960px" max-width="100%" bg-type="regular" hide-header>
            <div v-if="selectedTransaction" class="txd">
                <header class="txd__hero">
                    <div class="txd__hero-top">
                        <button type="button" class="txd__id" :title="isCancellationRecord ? 'Cancellation ID' : $t('frontend_analytics_transaction')"
                            @click="copyTransactionId">
                            <span class="txd__id-value">{{ selectedTransaction.transaction_id }}</span>
                            <copy-icon class="txd__id-icon" />
                            <span class="txd__id-copied" :class="{ 'is-visible': copied }">Copied</span>
                        </button>
                        <button type="button" class="txd__close" aria-label="Close" @click="closeModal">
                            <cross-icon class="txd__close-icon" />
                        </button>
                    </div>
                    <div class="txd__hero-main">
                        <span v-if="isCancellationRecord" class="txd__amount txd__amount--cancellation">Cancellation record</span>
                        <span v-else class="txd__amount">{{ selectedTransaction.human_readable_price }}</span>
                        <span class="txd__status" :class="`txd__status--${status.tone}`">
                            <span class="txd__status-dot"></span>{{ status.label }}
                        </span>
                    </div>
                    <p v-if="isCancellationRecord" class="txd__reference-amount">Reference amount: {{ selectedTransaction.human_readable_price }}</p>
                    <div class="txd__hero-meta">
                        <span class="txd__hero-product">{{ selectedTransaction.product_name }}</span>
                        <span v-if="selectedTransaction.transaction_type" class="txd__chip">{{ selectedTransaction.transaction_type }}</span>
                        <span v-if="selectedTransaction.gateway" class="txd__chip">{{ selectedTransaction.gateway }}</span>
                        <span class="txd__hero-date">{{ selectedTransaction.formatted_date_time }}</span>
                    </div>
                </header>

                <email-delivery-status v-if="!isCancellationRecord" :key="selectedTransaction.id" :status-url="`transactions/${selectedTransaction.id}/file-email`" :resend-url="`transactions/${selectedTransaction.id}/file-email`" />
                <div class="txd__body">
                    <section class="txd__main">
                        <nav class="txd__tabs" role="tablist">
                            <button v-for="tab in transactionDetailTabs" :key="tab.value" type="button" role="tab"
                                class="txd__tab" :class="{ 'is-active': transactionDetailsTab === tab.value }"
                                :aria-selected="transactionDetailsTab === tab.value"
                                @click="handleTransactionsDetailTabChange(tab.value)">
                                {{ tab.title }}
                                <span v-if="highLightTabs.includes(tab.value)" class="txd__tab-flag">Fraud Alert</span>
                            </button>
                        </nav>
                        <div class="txd__panel">
                            <transaction v-if="transactionDetailsTab === 'transaction'"
                                :selectedTransaction="selectedTransaction" :authenticatedUser="authenticatedUser"
                                :is-cancellation-record="isCancellationRecord" />
                            <customer v-else-if="transactionDetailsTab === 'customer'" :selectedTransaction="selectedTransaction"
                                @view-customer-transactions="$emit('view-customer-transactions', $event)" />
                            <custom-fields v-else-if="transactionDetailsTab === 'custom_fields'"
                                :selectedTransaction="selectedTransaction" />
                            <affiliate v-else-if="transactionDetailsTab === 'affiliate'"
                                :selectedTransaction="selectedTransaction" />
                            <retries v-else-if="transactionDetailsTab === 'retries'" :selectedTransaction="selectedTransaction" />
                        </div>
                    </section>

                    <aside v-if="isCancellationRecord" class="txd__rail">
                        <div class="txd__rail-title">Cancellation summary</div>
                        <div class="txd__renewal-status" role="status">
                            <strong>Cancellation recorded</strong>
                            <span>This record logs a cancellation. It does not represent a payment or a refund.</span>
                            <span>The amount shown is for reference only. To review payments or refunds, open the original payment transaction.</span>
                        </div>
                    </aside>
                    <aside v-else class="txd__rail">
                        <div class="txd__rail-title">Actions</div>

                        <button v-if="!selectedTransaction.is_partially_refunded &&
                            selectedTransaction.gateway.toLowerCase() == 'paypal' &&
                            selectedTransaction.total_amount > 0 &&
                            selectedTransaction.status == 'captured' &&
                            !selectedTransaction.is_refunded &&
                            !selectedTransaction.gateway_transaction_id.startsWith('I-')" type="button"
                            class="txd__action txd__action--primary" @click="refundTransaction()">
                            <dollar-icon class="txd__action-icon" />{{ $t('frontend_analytics_refund_transaction') }}
                        </button>
                        <button v-else-if="selectedTransaction.total_amount > 0 &&
                            selectedTransaction.status == 'captured' &&
                            !selectedTransaction.is_refunded &&
                            !selectedTransaction.is_partially_refunded" type="button"
                            class="txd__action txd__action--primary" @click="refundTransaction()">
                            <dollar-icon class="txd__action-icon" />{{ $t('frontend_analytics_refund_transaction') }}
                        </button>
                        <button v-else type="button" class="txd__action" disabled>
                            <dollar-icon class="txd__action-icon" />{{ $t('frontend_analytics_refunded_transaction') }}
                        </button>

                        <button v-if="selectedTransaction.total_amount > 0 &&
                            selectedTransaction.status == 'captured' &&
                            !selectedTransaction.is_refunded &&
                            !selectedTransaction.is_partially_refunded &&
                            !selectedTransaction.gateway_transaction_id.startsWith('I-')" type="button"
                            class="txd__action" @click="refundTransaction('partial_refund')">
                            <receipt-icon class="txd__action-icon" />{{ $t('frontend_analytics_refund_transaction_partially') }}
                        </button>

                        <div v-if="renewalNotice" class="txd__renewal-status" role="status">
                            <strong>{{ renewalNotice.title }}</strong>
                            <span v-if="renewalNotice.cancelledAt && renewalDate(renewalNotice.cancelledAt)">Cancelled {{ renewalDate(renewalNotice.cancelledAt) }}</span>
                            <span v-if="renewalNotice.paidThrough && renewalDate(renewalNotice.paidThrough)">Paid through {{ renewalDate(renewalNotice.paidThrough) }}</span>
                            <span v-if="renewalNotice.explanation">{{ renewalNotice.explanation }}</span>
                        </div>
                        <button v-else-if="selectedTransaction.status == 'captured' &&
                            selectedTransaction.is_active &&
                            selectedTransaction.payments_left &&
                            selectedTransaction.payments_left != 'Subscription ended'" type="button"
                            class="txd__action txd__action--danger" @click="openConfirmModal('cancelTransaction')">
                            <cross-icon class="txd__action-icon" />{{ $t('frontend_analytics_cancel_future_payments') }}
                        </button>
                        <button v-else type="button" class="txd__action" disabled>
                            <cross-icon class="txd__action-icon" />{{ $t('frontend_analytics_cancel_future_payments') }}
                        </button>

                        <button v-if="selectedTransaction.suspected_affiliate_fraud == 1" type="button"
                            class="txd__action" @click="openConfirmModal('unmarkAffiliateFraudTransaction')">
                            <check-circle-icon class="txd__action-icon" />{{ $t('frontend_analytics_mark_as_not_affiliate') }}
                        </button>

                        <button v-if="selectedTransaction.affiliate_id" type="button"
                            class="txd__action txd__action--danger" @click="openConfirmModal('removeAffiliateFromTransaction')">
                            <users-icon class="txd__action-icon" />{{ $t('frontend_analytics_remove_affiliate') }}
                        </button>

                        <button v-if="selectedTransaction.total_amount > 0 &&
                            selectedTransaction.status == 'failed' &&
                            !selectedTransaction.is_refunded &&
                            selectedTransaction.is_active" type="button"
                            class="txd__action" @click="openConfirmModal('retryTransaction')">
                            <reset-icon class="txd__action-icon" />{{ $t('frontend_analytics_retry_transaction') }}
                        </button>

                        <button v-if="selectedTransaction.status == 'captured'" type="button"
                            class="txd__action" @click="downloadTransactionInvoice">
                            <download-icon class="txd__action-icon" />{{ $t('frontend_analytics_download_invoice') }}
                        </button>

                        <button v-if="selectedTransaction.assisted_affiliate_id" type="button"
                            class="txd__action txd__action--danger" @click="openConfirmModal('removeAssist')">
                            <trash-icon class="txd__action-icon" />{{ $t('frontend_analytics_remove_assist') }}
                        </button>

                        <div v-if="selectedTransaction.refunded_note" class="txd__note">
                            <info-icon class="txd__note-icon" />
                            <span>{{ selectedTransaction.refunded_note }}</span>
                        </div>
                    </aside>
                </div>
            </div>
        </gd-modal>
        <confirm-modal
            name="confirm-transaction-detail-modal"
            :message-content="messageContent"
            :button-text="buttonText"
            button-size="auto"
            @confirm="applyAction"
        />
        <popup-alert v-if="alertProps.show" :variant="alertProps.variant" @close="alertProps.show = false">
            <template v-slot:title>
                {{ alertProps.title }}
            </template>
            <p>{{ alertProps.message }}</p>
        </popup-alert>
    </div>
</template>
<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { renewalDisplay, renewalDate } from '@/helpers/transactionRenewal.js';
import Transaction from '../tabs/Transaction';
import Customer from '../tabs/Customer';
import Affiliate from '../tabs/Affiliate';
import CustomFields from '../tabs/CustomFields';
import Retries from '../tabs/Retries';
import createLinkMixin from '@/mixins/createLinkMixin';
import InfoIcon from '@/assets/images/icons/InfoIcon.svg';
import CopyIcon from '@/assets/images/icons/CopyIcon.svg';
import CrossIcon from '@/assets/images/icons/CrossIcon.svg';
import DollarIcon from '@/assets/images/icons/DollarIcon.svg';
import ReceiptIcon from '@/assets/images/icons/ReceiptIcon.svg';
import DownloadIcon from '@/assets/images/icons/DownloadIcon.svg';
import ResetIcon from '@/assets/images/icons/ResetIcon.svg';
import UsersIcon from '@/assets/images/icons/UsersIcon.svg';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import CheckCircleIcon from '@/assets/images/icons/CheckCircleIcon.svg';

import EmailDeliveryStatus from '@/components/EmailDeliveryStatus.vue';

export default {
    components: {
        EmailDeliveryStatus,
        Transaction,
        Customer,
        Affiliate,
        CustomFields,
        Retries,
        InfoIcon,
        CopyIcon,
        CrossIcon,
        DollarIcon,
        ReceiptIcon,
        DownloadIcon,
        ResetIcon,
        UsersIcon,
        TrashIcon,
        CheckCircleIcon,
    },
    mixins: [createLinkMixin],
    props: {
        selectedTransaction: {
            type: Object,
            default: () => { },
        },
        highLightTabs: {
            required: true,
        },
        reloadData: {
            required: true,
            type: Function,
        },
        authenticatedUser: {
            default: null,
        },
    },
    watch: {
        // Always open on the first tab; a remembered tab may not exist for the next transaction.
        selectedTransaction() {
            this.transactionDetailsTab = 'transaction';
        },
    },
    data() {
        return {
            transactionDetailsTab: 'transaction',
            copied: false,
            copiedTimer: null,
            doNotTriggerWebhook: 0,
            chargeBackAffiliate: 0,
            actionType: '',
            messageContent: '',
            buttonText: '',
            alertProps: {
                show: false,
                variant: 'success',
                title: '',
                message: '',
            },
        }
    },
    methods: {
        renewalDate,
        closeModal() {
            this.$root.$emit('modal-close', 'transaction-detail-modal');
        },
        async copyTransactionId() {
            const id = this.selectedTransaction?.transaction_id;
            if (!id) return;
            try {
                await navigator.clipboard.writeText(id);
            } catch (e) {
                const el = document.createElement('textarea');
                el.value = id;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
            }
            this.copied = true;
            clearTimeout(this.copiedTimer);
            this.copiedTimer = setTimeout(() => { this.copied = false; }, 1400);
        },
        openConfirmModal(actionType) {
            if (this.isCancellationRecord || (actionType === 'cancelTransaction' && this.renewalNotice)) return;
            this.actionType = actionType;

            switch (actionType) {
                case 'cancelTransaction':
                    this.messageContent = this.$t('frontend_analytics_future_transactions_canceled');
                    this.buttonText = this.$t('frontend_analytics_future_transactions_canceled_confirm');
                    break;
                case 'unmarkAffiliateFraudTransaction':
                    this.messageContent = this.$t('frontend_analytics_transaction_not_suspicious');
                    this.buttonText = this.$t('frontend_analytics_markas_notsuspcious');
                    break;
                case 'removeAffiliateFromTransaction':
                    this.messageContent = this.$t('frontend_remove_affiliate_warning');
                    this.buttonText = this.$t('frontend_remove_affiliate_confirm');
                    break;
                case 'retryTransaction':
                    this.messageContent = this.$t('frontend_retry_transaction_warning', {appTitle: this.whitelabelAppData('sell').title});
                    this.buttonText = this.$t('frontend_retry_transaction_confirm');
                    break;
                case 'removeAssist':
                    this.messageContent = this.$t('frontend_remove_assist_warning');
                    this.buttonText = this.$t('frontend_remove_assist_confirm');
                    break;
            }

            this.$root.$emit('modal-open', 'confirm-transaction-detail-modal');
        },
        applyAction() {
            switch (this.actionType) {
                case 'cancelTransaction':
                    this.cancelTransaction();
                    break;
                case 'unmarkAffiliateFraudTransaction':
                    this.unmarkAffiliateFraudTransaction();
                    break;
                case 'removeAffiliateFromTransaction':
                    this.removeAffiliateFromTransaction();
                    break;
                case 'retryTransaction':
                    this.retryTransaction();
                    break;
                case 'removeAssist':
                    this.removeAssist();
                    break;
            }
        },
        handleTransactionsDetailTabChange(newTab) {
            this.transactionDetailsTab = newTab;
        },
        async cancelTransaction() {
            if (this.renewalNotice) return;
            const self = this;
            this.$loader.start('cancelTransaction');
            try {
            const cancelProcessData = await axios.post('/transactions/cancel', {
                transaction_id: self.selectedTransaction.id,
            });
            await this.reloadData();

            const cancelData = cancelProcessData.data;
            const cancelStatus = cancelData.status;
            if (cancelStatus == 'success') {
                this.alertProps = {
                    show: true,
                    variant: 'success',
                    title: cancelData.already_cancelled ? 'Renewal already cancelled' : this.$t('frontend_analytics_future_transactions_canceled_success'),
                    message: cancelData.already_cancelled ? 'This subscription was already cancelled. No additional cancellation was processed.' : this.$t('frontend_analytics_future_transactions_canceled_success_message'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            } else if (cancelStatus == 'manual') {
                this.alertProps = {
                    show: true,
                    variant: 'info',
                    title: this.$t('frontend_analytics_manual_cancelation_needed'),
                    message: cancelData.message,
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            } else {
                this.alertProps = {
                    show: true,
                    variant: 'error',
                    title: this.$t('frontend_analytics_future_transactions_canceled_failed'),
                    message: this.$t('frontend_analytics_future_transactions_canceled_failed_message'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            }

            } catch (error) {
                this.alertProps = { show: true, variant: 'error', title: 'Cancellation not confirmed', message: 'Refresh this transaction to check its current renewal status before trying again.' };
                this.$root.$emit('modal-close', 'confirm-transaction-detail-modal');
            } finally {
                this.$loader.end('cancelTransaction');
            }
        },
        async unmarkAffiliateFraudTransaction() {
            const self = this;
            this.$loader.start('unmarkAffiliateFraudTransaction');
            const notSuspiciousResponseData = await axios.post(
                '/transactions/mark-not-suspicious',
                {
                    transaction_id: self.selectedTransaction.id,
                }
            );
            await this.reloadData();

            const notSuspiciousData = notSuspiciousResponseData.data;
            const notSuspiciousStatus = notSuspiciousData.status;
            if (notSuspiciousStatus == 'success') {
                this.alertProps = {
                    show: true,
                    variant: 'success',
                    title: this.$t('frontend_record_successful'),
                    message: this.$t('frontend_marked_as_not_suspicious_successfully'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            } else {
                this.alertProps = {
                    show: true,
                    variant: 'error',
                    title: this.$t('frontend_record_failed'),
                    message: this.$t('frontend_unable_to_mark_transaction_not_suspicious'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            }

            this.$loader.end('unmarkAffiliateFraudTransaction');
        },
        async removeAffiliateFromTransaction() {
            const self = this;
            this.$loader.start('removeAffiliateFromTransaction');
            const removeAffiliateResponseData = await axios.post(
                '/transactions/remove-affiliate',
                {
                    transaction_id: self.selectedTransaction.id,
                }
            );
            await this.reloadData();

            const removeAffiliateData = removeAffiliateResponseData.data;
            const removeAffiliateStatus = removeAffiliateData.status;
            if (removeAffiliateStatus == 'success') {
                this.alertProps = {
                    show: true,
                    variant: 'success',
                    title: this.$t('frontend_affiliate_removal_successful_title'),
                    message: this.$t('frontend_affiliate_removal_successful_message'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            } else {
                this.alertProps = {
                    show: true,
                    variant: 'error',
                    title: this.$t('frontend_affiliate_removal_failed_title'),
                    message: this.$t('frontend_affiliate_removal_failed_message'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            }

            this.$loader.end('removeAffiliateFromTransaction');
        },
        async retryTransaction() {
            const self = this;
            this.$loader.start('retryTransaction');
            const manualRetryProcessData = await axios.post(
                '/transactions/manual-retry',
                {
                    transaction_id: self.selectedTransaction.id,
                }
            );
            await this.reloadData();

            const manualRetryData = manualRetryProcessData.data;
            const manualRetryStatus = manualRetryData.status;
            if (manualRetryStatus == 'success') {
                this.alertProps = {
                    show: true,
                    variant: 'success',
                    title: this.$t('frontend_dunning_attempt_successful_title'),
                    message: this.$t('frontend_dunning_attempt_successful_message'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            } else {
                this.alertProps = {
                    show: true,
                    variant: 'error',
                    title: this.$t('frontend_dunning_attempt_unsuccessful_title'),
                    message: this.$t('frontend_dunning_attempt_unsuccessful_message'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            }

            this.$loader.end('retryTransaction');
        },
        async downloadTransactionInvoice() {
            this.$loader.start('downloadTransactionInvoice');
            const paramsData = { transaction_id: this.selectedTransaction.id };
            await this.createOpenLink('transactions/generate-invoice-download-link', paramsData);
            this.$loader.end('downloadTransactionInvoice');

        },
        async removeAssist() {
            const self = this;
            this.$loader.start('removeAssist');
            const assistProcessData = await axios.post(
                '/transactions/remove-assist',
                {
                    transaction_id: self.selectedTransaction.id,
                }
            );
            await this.reloadData();

            const assistData = assistProcessData.data;
            const assistStatus = assistData.status;
            if (assistStatus == 'success') {
                this.alertProps = {
                    show: true,
                    variant: 'success',
                    title: this.$t('frontend_removal_of_assist_successful_title'),
                    message: this.$t('frontend_removal_of_assist_successful_message'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            } else {
                this.alertProps = {
                    show: true,
                    variant: 'error',
                    title: this.$t('frontend_removal_of_assist_failed_title'),
                    message: this.$t('frontend_removal_of_assist_failed_message'),
                };
                this.$root.$emit('modal-close', 'transaction-detail-modal');
            }

            this.$loader.end('removeAssist');
        },
        async refundTransactionStep1(type) {
            const result = await Swal.fire({
                title: this.$t(`frontend_analytics_${type}_transactions`),
                html: this.$t(`frontend_analytics_${type}_transactions_text`),
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: this.$t(`frontend_process_${type}_with`) + this.selectedTransaction.gateway,
                denyButtonText: this.$t(`frontend_analytics_mark_${type}`),
                cancelButtonText: 'Cancel',
                showCloseButton: true,

            });

            const processGateway = result.isConfirmed ? true : false;
            const cancelRefund = result.isDismissed ? true : false;

            return { processGateway, cancelRefund };
        },

        async refundTransactionStep2(type, messageAlert) {
            const result = await Swal.fire({
                input: 'text',
                inputLabel: this.$t('frontend_analytics_transactions_note'),
                inputPlaceholder: this.$t('frontend_analytics_transactions_enter_note'),
                inputValidator: (value) => { },
                title: this.$t('frontend_sure'),
                text: messageAlert,
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t(`frontend_confirm_${type}_transaction`),
            });

            const textnote = result.value;
            const cancelRefund = result.isDismissed ? true : false;

            return { textnote, cancelRefund };
        },

        async refundTransactionStep3(type) {
            if (type != 'partial_refund') {
                return { refundedValue: 0, cancelRefund: false };
            }

            const result = await Swal.fire({
                title: this.$t('frontend_partial_refund_amount'),
                input: 'text',
                inputPlaceholder: this.$t('frontend_enter_amount'),
                inputValidator: (value) => {
                    const numberRegex =
                        /^\s*[+-]?(\d+|\.\d+|\d+\.\d+|\d+\.)(e[+-]?\d+)?\s*$/;
                    if (!value) {
                        return this.$t('frontend_analytics_enter_valid_number');
                    }
                    if (!numberRegex.test(value)) {
                        return this.$t('frontend_analytics_enter_valid_number');
                    }
                    if (value <= 0) {
                        return this.$t('frontend_analytics_enter_valid_number');
                    }
                    if (value > this.selectedTransaction.total_amount / 100) {
                        return this.$t('frontend_analytics_enter_correct_amount');
                    }
                },
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('frontend_confirm_partial_refund_transaction'),
                onOpen: () => {
                    const sp1 = Swal.getContent().querySelector('.swal2-input');
                    const sp2 = Swal.getContent().querySelector('#swal2-content');
                    const parentDiv = sp2.parentNode;
                    parentDiv.insertBefore(sp1, sp2);
                },
                customClass: {
                    title: 'refundSwalTitle',
                },
            });

            const refundAmount = result.value;
            const cancelRefund = result.isDismissed ? true : false;

            return { refundAmount, cancelRefund };
        },

        async refundTransactionStep4() {
            const triggerResult = await this.showManualRefundConfirmation({
                variant: 'triggers',
                eyebrow: this.$t('frontend_manual_refund_triggers_eyebrow'),
                title: this.$t('frontend_manual_refund_trigger_question_title'),
                message: this.$t('frontend_manual_refund_trigger_question_text'),
                detail: this.$t('frontend_manual_refund_trigger_question_detail'),
                confirmButtonText: this.$t('frontend_manual_refund_yes_run_triggers'),
            });

            const emailResult = await this.showManualRefundConfirmation({
                variant: 'email',
                eyebrow: this.$t('frontend_manual_refund_email_eyebrow'),
                title: this.$t('frontend_manual_refund_email_question_title'),
                message: this.$t('frontend_manual_refund_email_question_text'),
                detail: this.$t('frontend_manual_refund_email_question_detail'),
                confirmButtonText: this.$t('frontend_manual_refund_yes_send_email'),
            });

            return {
                triggerRefundActions: triggerResult.value === true,
                sendCustomerRefundEmail: emailResult.value === true,
                cancelRefund: false,
            };
        },

        async showManualRefundConfirmation({ variant, eyebrow, title, message, detail, confirmButtonText }) {
            const icon = variant === 'email'
                ? `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 6.5h17v11h-17z"/><path d="m4 7 8 6 8-6"/></svg>`
                : `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="6" cy="6" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="12" cy="18" r="2"/><path d="m7.7 7.1 8.5.8M7.2 7.7l3.7 8.5m5.7-6.3-3.2 6.2"/></svg>`;

            return Swal.fire({
                html: `
                    <section class="manual-refund-confirmation manual-refund-confirmation--${variant}">
                        <div class="manual-refund-confirmation__icon">${icon}</div>
                        <span class="manual-refund-confirmation__eyebrow">${eyebrow}</span>
                        <h2 class="manual-refund-confirmation__title">${title}</h2>
                        <p class="manual-refund-confirmation__message">${message}</p>
                        <div class="manual-refund-confirmation__detail">${detail}</div>
                    </section>
                `,
                showCancelButton: true,
                confirmButtonText,
                cancelButtonText: this.$t('frontend_no'),
                allowOutsideClick: false,
                allowEscapeKey: false,
                reverseButtons: true,
                buttonsStyling: false,
                customClass: {
                    popup: 'manual-refund-confirmation-popup',
                    actions: 'manual-refund-confirmation__actions',
                    confirmButton: 'manual-refund-confirmation__button manual-refund-confirmation__button--confirm',
                    cancelButton: 'manual-refund-confirmation__button manual-refund-confirmation__button--cancel',
                },
            });
        },

        async refundTransaction(type = "refund") {
            if (this.isCancellationRecord) return;
            let accept;
            let processGateway;
            let textnote;
            let cancelRefund;
            let refundAmount;
            let triggerRefundActions = false;
            let sendCustomerRefundEmail = false;

            ({ processGateway, cancelRefund } = await this.refundTransactionStep1(type));

            if (cancelRefund) {
                return;
            }

            if (!processGateway) {
                ({ triggerRefundActions, sendCustomerRefundEmail, cancelRefund } = await this.refundTransactionStep4());

                if (cancelRefund) {
                    return;
                }
            }

            ({ refundAmount, cancelRefund } = await this.refundTransactionStep3(type));

            if (cancelRefund) {
                return;
            }

            const messageAlert = processGateway ? this.$t('frontend_confirm_refund_with_gateway', {
                amount: type == 'refund' ? this.selectedTransaction.human_readable_price : refundAmount,
                gateway: this.selectedTransaction.gateway
            }) : this.$t(`frontend_confirm_mark_transaction_${type}`);

            ({ textnote, cancelRefund } = await this.refundTransactionStep2(type, messageAlert));

            if (cancelRefund) {
                return;
            }

            let queue = [{
                title: this.$t('frontend_sure'),
                text: this.$t('frontend_analytics_refund_warning'),
                confirmButtonText: this.$t(`frontend_confirm_${type}_transaction`),
            }];

            if (this.selectedTransaction.is_refund_period_over) {
                queue.unshift({
                    title: '<span style="color: #dd3333;">' + this.$t('frontend_analytics_exceeded') + '</span>',
                    text: this.$t('frontend_analytics_exceeded_text'),
                })
            }

            ({ value: accept } = await Swal.mixin({
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('frontend_analytics_process_to_refund'),
            }).queue(queue));

            if (accept) {
                await this.fireRefundTransaction(this.selectedTransaction.id, type, processGateway, textnote, refundAmount, triggerRefundActions, sendCustomerRefundEmail);
            }
        },

        async fireRefundTransaction(transactionId, type, processGateway, textnote, refundAmount, triggerRefundActions, sendCustomerRefundEmail) {
                this.$loader.start('refundTransaction');
                const refundProcessData = await axios.post('/transactions/refund', {
                    transaction_id: transactionId,
                    process_gateway: processGateway,
                    note: textnote,
                    amount: refundAmount,
                    is_partial_refund: type == 'partial_refund',
                    trigger_refund_actions: triggerRefundActions,
                    send_customer_refund_email: sendCustomerRefundEmail,
                });

                await this.reloadData();

                const refundData = refundProcessData.data;
                const refundStatus = refundData.status;
                if (refundStatus == 'success') {
                    this.alertProps = {
                        show: true,
                        variant: 'success',
                        title: this.$t('frontend_analytics_refund_sucess'),
                        message: this.$t('frontend_analytics_transaction_refund_sucess'),
                    };
                    this.$root.$emit('modal-close', 'transaction-detail-modal');
                } else {
                    this.alertProps = {
                        show: true,
                        variant: 'error',
                        title: this.$t('frontend_analytics_refund_failed'),
                        message: this.$t('frontend_analytics_refund_failed_message') + refundData.message,
                    };
                    this.$root.$emit('modal-close', 'transaction-detail-modal');
                }

                this.$loader.end('refundTransaction');
        }

    },

    computed: {
        isCancellationRecord() {
            const transaction = this.selectedTransaction || {};
            return ['cancelled', 'canceled'].includes(String(transaction.status || '').toLowerCase()) ||
                String(transaction.transaction_type || '').toLowerCase() === 'cancellation';
        },
        renewalNotice() { return this.isCancellationRecord ? null : renewalDisplay(this.selectedTransaction); },
        status() {
            const t = this.selectedTransaction || {};
            if (this.isCancellationRecord) return { label: 'Cancelled', tone: 'neutral' };
            if (t.is_refunded) {
                return { label: this.$t('frontend_analytics_refunded_transaction'), tone: 'neutral' };
            }
            if (t.is_partially_refunded) {
                return { label: 'Partially refunded', tone: 'warning' };
            }
            if (t.status == 'captured') {
                return { label: 'Captured', tone: 'success' };
            }
            if (t.status == 'failed') {
                return { label: 'Failed', tone: 'danger' };
            }
            const raw = String(t.status || '');
            return { label: raw.charAt(0).toUpperCase() + raw.slice(1), tone: 'neutral' };
        },
        transactionDetailTabs() {
            if (this.selectedTransaction && this.selectedTransaction.has_failed_previously) {
                return [
                    { title: this.isCancellationRecord ? 'Cancellation' : this.$t('frontend_analytics_transaction'), value: 'transaction' },
                    { title: this.$t('frontend_analytics_customer'), value: 'customer' },
                    { title: this.$t('frontend_analytics_affiliate'), value: 'affiliate' },
                    { title: this.$t('frontend_analytics_failed_transaction_logs'), value: 'retries' },
                ];
            }
            if (this.selectedTransaction && this.selectedTransaction.buyer_custom_fields) {
                return [
                    { title: this.isCancellationRecord ? 'Cancellation' : this.$t('frontend_analytics_transaction'), value: 'transaction' },
                    { title: this.$t('frontend_analytics_customer'), value: 'customer' },
                    { title: this.$t('frontend_analytics_custom_fields'), value: 'custom_fields' },
                    { title: this.$t('frontend_analytics_affiliate'), value: 'affiliate' },
                ];
            }
            return [
                { title: this.isCancellationRecord ? 'Cancellation' : this.$t('frontend_analytics_transaction'), value: 'transaction', },
                { title: this.$t('frontend_analytics_customer'), value: 'customer' },
                { title: this.$t('frontend_analytics_affiliate'), value: 'affiliate' },
            ];
        },
    }
}
</script>
<style lang="scss">
.txd__renewal-status {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px;
    border: 1px solid #bbd7ca;
    border-radius: 10px;
    background: #f0faf5;
    color: #184b35;
    font-size: 12px;
    strong { font-size: 14px; }
}

// Transaction detail modal — self-contained styling, namespaced under .txd so
// nothing leaks to other pages. Tab partials (../tabs/*.vue) share these classes.
.gd-modal.transaction-detail-modal > .gd-modal__content > div {
    border-radius: 16px;
    box-shadow: 0 24px 64px -12px rgba(24, 34, 48, 0.28), 0 0 0 1px rgba(24, 34, 48, 0.04);
    overflow: hidden;
}

.txd {
    --txd-primary: #f22987;
    --txd-primary-soft: #fff0f7;
    --txd-fg: #182230;
    --txd-muted: #667085;
    --txd-faint: #98a2b3;
    --txd-border: #e2e8f0;
    --txd-surface: #f8fafc;
    --txd-success: #16a34a;
    --txd-success-soft: #ecfdf3;
    --txd-warning: #b45309;
    --txd-warning-soft: #fffbeb;
    --txd-danger: #dc2626;
    --txd-danger-soft: #fef2f2;
    --txd-mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;

    color: var(--txd-fg);
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 4rem);
    text-align: left;

    button {
        font: inherit;
        color: inherit;
    }

    // ---------- hero ----------
    &__hero {
        padding: 20px 28px 22px;
        border-bottom: 1px solid var(--txd-border);
        background:
            radial-gradient(120% 140% at 100% 0%, var(--txd-primary-soft) 0%, rgba(255, 240, 247, 0) 55%),
            #fff;
    }

    &__hero-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 14px;
    }

    &__id {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 5px 10px;
        border: 1px solid var(--txd-border);
        border-radius: 8px;
        background: #fff;
        font-family: var(--txd-mono);
        font-size: 12.5px;
        letter-spacing: 0.01em;
        color: var(--txd-muted);
        cursor: pointer;
        transition: border-color 120ms ease, color 120ms ease;

        &:hover,
        &:focus-visible {
            border-color: #cbd5e1;
            color: var(--txd-fg);
            outline: none;
        }

        &:focus-visible {
            box-shadow: 0 0 0 3px rgba(242, 41, 135, 0.2);
        }
    }

    &__id-icon {
        width: 13px;
        height: 13px;
        fill: currentColor;
        opacity: 0.7;
    }

    &__id-copied {
        position: absolute;
        left: 50%;
        top: calc(100% + 6px);
        transform: translate(-50%, -4px);
        padding: 3px 8px;
        border-radius: 6px;
        background: var(--txd-fg);
        color: #fff;
        font-family: inherit;
        font-size: 11.5px;
        font-weight: 600;
        opacity: 0;
        pointer-events: none;
        transition: opacity 140ms ease, transform 140ms ease;

        &.is-visible {
            opacity: 1;
            transform: translate(-50%, 0);
        }
    }

    &__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border-radius: 999px;
        border: 0;
        background: transparent;
        color: var(--txd-muted);
        cursor: pointer;
        transition: background 120ms ease, color 120ms ease;

        &:hover,
        &:focus-visible {
            background: rgba(24, 34, 48, 0.06);
            color: var(--txd-fg);
            outline: none;
        }
    }

    &__close-icon {
        width: 18px;
        height: 18px;
        fill: currentColor;
    }

    &__hero-main {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 6px 14px;
    }

    &__amount {
        font-size: 36px;
        font-weight: 800;
        line-height: 1;
        letter-spacing: -0.02em;
        font-variant-numeric: tabular-nums;
        color: var(--txd-fg);
    }

    &__amount--cancellation { font-size: 30px; }

    &__reference-amount {
        margin: 12px 0 0;
        color: var(--txd-muted);
        font-size: 14px;
    }

    &__status {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 4px 11px 4px 9px;
        border-radius: 999px;
        font-size: 12.5px;
        font-weight: 700;
        line-height: 1.3;
        position: relative;
        top: -3px;

        &--success { background: var(--txd-success-soft); color: var(--txd-success); }
        &--warning { background: var(--txd-warning-soft); color: var(--txd-warning); }
        &--danger { background: var(--txd-danger-soft); color: var(--txd-danger); }
        &--neutral { background: #f1f5f9; color: #475467; }
    }

    &__status-dot {
        width: 7px;
        height: 7px;
        border-radius: 999px;
        background: currentColor;
    }

    &__hero-meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px 10px;
        margin-top: 12px;
        font-size: 13.5px;
        color: var(--txd-muted);
    }

    &__hero-product {
        color: var(--txd-fg);
        font-weight: 600;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__chip {
        padding: 2px 9px;
        border-radius: 999px;
        border: 1px solid var(--txd-border);
        background: #fff;
        font-size: 12px;
        font-weight: 600;
        color: var(--txd-muted);
        text-transform: capitalize;
    }

    &__hero-date {
        margin-left: auto;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    // ---------- body ----------
    &__body {
        display: flex;
        min-height: 0;
        flex: 1 1 auto;
    }

    &__main {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        min-height: 0;
    }

    &__tabs {
        display: flex;
        flex-shrink: 0;
        gap: 4px;
        padding: 6px 28px 0;
        border-bottom: 1px solid var(--txd-border);
    }

    &__tab {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 10px 11px;
        border: 0;
        background: transparent;
        font-size: 14px;
        font-weight: 600;
        color: var(--txd-muted);
        cursor: pointer;
        transition: color 120ms ease;

        &::after {
            content: '';
            position: absolute;
            left: 10px;
            right: 10px;
            bottom: -1px;
            height: 2px;
            border-radius: 2px 2px 0 0;
            background: var(--txd-primary);
            transform: scaleX(0);
            transition: transform 160ms ease;
        }

        &:hover { color: var(--txd-fg); }

        &:focus-visible {
            outline: none;
            border-radius: 6px;
            box-shadow: inset 0 0 0 2px rgba(242, 41, 135, 0.35);
        }

        &.is-active {
            color: var(--txd-fg);

            &::after { transform: scaleX(1); }
        }
    }

    &__tab-flag {
        padding: 2px 7px;
        border-radius: 999px;
        background: var(--txd-danger-soft);
        color: var(--txd-danger);
        font-size: 11px;
        font-weight: 700;
        line-height: 1.4;
    }

    &__panel {
        padding: 6px 28px 24px;
        overflow-y: auto;
        min-height: 300px;
    }

    // Key / value grid used by every tab
    &__fields {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 32px;
    }

    &__field {
        display: flex;
        flex-direction: column;
        gap: 3px;
        padding: 13px 0 12px;
        border-bottom: 1px solid var(--txd-border);
        min-width: 0;

        &--wide { grid-column: 1 / -1; }
    }

    &__label {
        font-size: 12.5px;
        font-weight: 600;
        color: var(--txd-muted);
    }

    &__value {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14.5px;
        font-weight: 500;
        color: var(--txd-fg);
        overflow-wrap: anywhere;

        &--mono {
            font-family: var(--txd-mono);
            font-size: 13px;
            letter-spacing: 0.01em;
        }

        &--empty { color: var(--txd-faint); font-weight: 400; }
    }

    &__edit {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        border-radius: 6px;
        border: 0;
        background: transparent;
        color: var(--txd-faint);
        cursor: pointer;
        transition: background 120ms ease, color 120ms ease;

        svg { width: 14px; height: 14px; fill: currentColor; }

        &:hover,
        &:focus-visible {
            background: var(--txd-primary-soft);
            color: var(--txd-primary);
            outline: none;
        }
    }

    &__callout {
        display: flex;
        gap: 10px;
        margin-top: 18px;
        padding: 12px 14px;
        border-radius: 10px;
        background: var(--txd-danger-soft);
        color: #7f1d1d;
        font-size: 13.5px;
        line-height: 1.5;

        strong { color: var(--txd-danger); }
    }

    &__empty {
        padding: 40px 0;
        text-align: center;
        color: var(--txd-muted);
        font-size: 14px;
    }

    // Retry log table (vue-tables-2)
    &__table {
        margin-top: 10px;

        .VueTables__search,
        .VueTables__limit { display: none; }

        table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13.5px;
        }

        th {
            padding: 8px 12px;
            text-align: left;
            font-size: 12.5px;
            font-weight: 600;
            color: var(--txd-muted);
            border-bottom: 1px solid var(--txd-border);
            background: var(--txd-surface);
        }

        td {
            padding: 11px 12px;
            border-bottom: 1px solid var(--txd-border);
            color: var(--txd-fg);
            vertical-align: middle;
        }

        .VuePagination { margin-top: 10px; }
    }

    // ---------- action rail ----------
    &__rail {
        flex: 0 0 292px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 18px 20px 22px;
        border-left: 1px solid var(--txd-border);
        background: var(--txd-surface);
        overflow-y: auto;
    }

    &__rail-title {
        font-size: 12.5px;
        font-weight: 600;
        color: var(--txd-muted);
        margin-bottom: 2px;
    }

    &__action {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 10px 12px;
        border-radius: 10px;
        border: 1px solid var(--txd-border);
        background: #fff;
        font-size: 13.5px;
        font-weight: 600;
        line-height: 1.3;
        text-align: left;
        color: var(--txd-fg);
        cursor: pointer;
        transition: border-color 120ms ease, background 120ms ease, color 120ms ease, transform 120ms ease, box-shadow 120ms ease;

        &:hover:not(:disabled) {
            border-color: #cbd5e1;
            background: #fff;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px -4px rgba(24, 34, 48, 0.18);
        }

        &:active:not(:disabled) { transform: translateY(0); }

        &:focus-visible {
            outline: none;
            box-shadow: 0 0 0 3px rgba(242, 41, 135, 0.25);
        }

        &:disabled {
            cursor: default;
            color: var(--txd-faint);
            background: transparent;
            border-style: dashed;
            border-color: var(--txd-border);
        }

        &--primary {
            background: var(--txd-primary);
            border-color: var(--txd-primary);
            color: #fff;
            box-shadow: 0 8px 20px -6px rgba(242, 41, 135, 0.45);

            &:hover:not(:disabled) {
                background: #e01d7a;
                border-color: #e01d7a;
                box-shadow: 0 10px 24px -6px rgba(242, 41, 135, 0.55);
            }
        }

        &--danger:hover:not(:disabled) {
            border-color: #fecaca;
            background: var(--txd-danger-soft);
            color: var(--txd-danger);
        }
    }

    &__action-icon {
        flex: 0 0 auto;
        width: 15px;
        height: 15px;
        fill: currentColor;
        opacity: 0.85;
    }

    &__note {
        display: flex;
        gap: 9px;
        margin-top: auto;
        padding: 11px 12px;
        border-radius: 10px;
        background: var(--txd-warning-soft);
        color: #78350f;
        font-size: 12.5px;
        line-height: 1.5;
    }

    &__note-icon {
        flex: 0 0 auto;
        width: 15px;
        height: 15px;
        margin-top: 2px;
        fill: currentColor;
        opacity: 0.8;
    }

    // ---------- responsive ----------
    @media (max-width: 760px) {
        overflow-y: auto; // the whole card scrolls; no nested scroll areas

        &__hero { padding: 16px 18px 18px; }
        &__amount { font-size: 30px; }
        &__hero-date { margin-left: 0; width: 100%; }
        &__body { flex: none; flex-direction: column; }
        &__tabs { padding: 4px 18px 0; overflow-x: auto; }
        &__panel { padding: 4px 18px 18px; min-height: 0; overflow: visible; }
        &__fields { grid-template-columns: 1fr; }
        &__rail {
            flex-basis: auto;
            border-left: 0;
            border-top: 1px solid var(--txd-border);
            overflow: visible;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after { transition: none !important; }
    }
}
</style>
